import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import config from '@/config';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { getOrganizations } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';

import { SettingsSectionLayout } from '../components/Layout/Layout';

import { NewOrganizationForm } from './NewOrganizationForm';

export const Organizations = () => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const [currentOrganization, setCurrentOrganization] = useCurrentOrganization();

  const [search, setSearch] = useState('');
  const [isNewOrganizationOpen, setIsNewOrganizationOpen] = useState(false);

  const normalizedSearch = search.trim().toLowerCase();
  const organizations = getOrganizations(currentSession)
    .filter(
      (organization) =>
        !normalizedSearch ||
        organization.name.toLowerCase().includes(normalizedSearch) ||
        organization.slug.toLowerCase().includes(normalizedSearch)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const switchOrganization = (organization: { slug: string }) => {
    setCurrentOrganization?.(organization, '/settings_beta/team');
  };

  return (
    <SettingsSectionLayout
      title={<Trans id="settings-section.organizations">Organizations</Trans>}
      basePath="/settings_beta/support/organizations"
    >
      <Stack gap="1rem">
        <Stack row gap="1rem" alignItems="center">
          <InputSearch
            ariaLabel={t({
              id: 'settings-section.organizations.search.label',
              message: 'Search organizations',
            })}
            placeholder={t({
              id: 'settings-section.organizations.search.placeholder',
              message: 'Search by name or slug...',
            })}
            value={search}
            onChange={setSearch}
            style={{ flex: 1 }}
          />
          <Button variant="primary" size="medium" onPress={() => setIsNewOrganizationOpen(true)}>
            <Trans id="settings-section.organizations.new">New organization</Trans>
          </Button>
        </Stack>

        {organizations.length === 0 ? (
          <EmptyState
            subtitle={
              <Paragraph>
                <Trans id="settings-section.organizations.no-results">
                  No organization is matching your search.
                </Trans>
              </Paragraph>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table columnWidths={['2fr', '1fr', 'auto']} bordered>
            <Header>
              <Row>
                <Column>
                  <Trans id="settings-section.organizations.table.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings-section.organizations.table.slug">Slug</Trans>
                </Column>
                <Column>
                  <span className="visually-hidden">
                    <Trans id="settings-section.organizations.table.actions">Actions</Trans>
                  </span>
                </Column>
              </Row>
            </Header>
            <Body>
              {organizations.map((organization) => {
                const isCurrent = organization.id === currentOrganization?.id;

                return (
                  <Row key={organization.id}>
                    <Cell>
                      <Stack row gap="0.75rem" alignItems="center">
                        <img
                          src={`${config.apiUrl}/organizations/${organization.slug}/logo?alias=1&type=square`}
                          alt=""
                          width={24}
                          height={24}
                        />
                        <Paragraph weight="medium">{organization.name}</Paragraph>
                        {isCurrent && (
                          <Badge color="primary" size="small">
                            <Trans id="settings-section.organizations.current">Current</Trans>
                          </Badge>
                        )}
                      </Stack>
                    </Cell>
                    <Cell>
                      <Paragraph size="200" color="secondary">
                        {organization.slug}
                      </Paragraph>
                    </Cell>
                    <Cell>
                      {!isCurrent && (
                        <Button
                          variant="secondary"
                          size="small"
                          onPress={() => switchOrganization(organization)}
                        >
                          <Trans id="settings-section.organizations.switch">Switch</Trans>
                        </Button>
                      )}
                    </Cell>
                  </Row>
                );
              })}
            </Body>
          </Table>
        )}
      </Stack>

      <Dialog
        isOpen={isNewOrganizationOpen}
        onOpenChange={setIsNewOrganizationOpen}
        title={<Trans id="support.general.new-organization.title">Create a new organization</Trans>}
        style={{ maxWidth: '32rem' }}
      >
        <main>
          <NewOrganizationForm />
        </main>
      </Dialog>
    </SettingsSectionLayout>
  );
};
